import { useState } from "react";
import { Home, ListChecks, Map as MapIcon, History, User, Navigation, Phone, LogOut, ChevronRight, AlertTriangle } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { PartnerJob } from "@/components/PartnerJob";
import { PartnerPerformance, ServiceHours } from "@/components/PartnerPerformance";
import { PartnerAlerts, StarBanner } from "@/components/PartnerAlerts";
import { Eligibility } from "@/components/PartnerEligibility";
import { usePT, LangSwitch } from "@/lib/pt";
import { PartnerEarnings, PartnerKyc } from "@/components/PartnerExtras";
import { PartnerMap, BusyAreas, RingTest } from "@/components/PartnerLive";

import { Stars } from "@/components/RatePartner";
import { PartnerProfile, PartnerRefer } from "@/components/PartnerProfile";
import { VerifiedTick } from "@/components/VerifiedTick";
import { PartnerLoginSetup } from "@/components/PartnerLoginSetup";

const MyRating = ({ r }) => (
  <div className="space-y-2 rounded-3xl bg-white p-4" data-testid="partner-rating">
    <div className="flex items-center justify-between">
      <p className="font-bold text-[#0D3B2B]">My rating</p>
      <span className="flex items-center gap-2"><Stars value={Math.round(r.avg || 0)} size="h-4 w-4" /><b data-testid="partner-rating-avg">{r.avg ?? "New"}</b><span className="text-xs text-[#7B968B]">({r.count})</span></span>
    </div>
    <p className="text-xs text-[#4A6358]">Higher-rated partners get new pickups first. Be on time, weigh fairly and be polite.</p>
    {r.recent.filter((x) => x.comment).map((x, i) => <p key={i} className="rounded-xl bg-[#F4F8F5] p-2 text-xs text-[#0D3B2B]">{"★".repeat(x.stars)} “{x.comment}”</p>)}
  </div>
);

export const TABS = [["home", "Home", Home], ["pickups", "Pickups", ListChecks], ["map", "Map", MapIcon], ["history", "History", History], ["me", "Me", User]];

export const PartnerNav = ({ tab, setTab, count }) => {
  const { pt } = usePT();
  return (
  <nav className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-md border-t border-[#D2E0D8] bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md" data-testid="partner-nav">
    {TABS.map(([k, l, Icon]) => (
      <button key={k} onClick={() => { setTab(k); window.scrollTo(0, 0); }} data-testid={`partner-tab-${k}`}
        className={`relative flex h-[72px] flex-1 flex-col items-center justify-center gap-0.5 text-xs font-bold transition-colors ${tab === k ? "text-[#0D3B2B]" : "text-[#7B968B]"}`}>
        <span className={`flex h-9 w-14 items-center justify-center rounded-full transition-colors ${tab === k ? "bg-[#2E9E6B]/15" : ""}`}><Icon className="h-7 w-7" /></span>{pt(l)}
        {k === "pickups" && count > 0 && <span className="absolute right-[14%] top-1 min-w-[22px] rounded-full bg-[#E05C3A] px-1 text-xs text-white" data-testid="partner-pickups-badge">{count}</span>}
      </button>
    ))}
  </nav>
  );
};

export { HomeTab } from "@/components/PartnerHome";

const MAX_STOPS = 10;
const legs = (stops) => {
  const out = [];
  for (let i = 0; i < stops.length; i += MAX_STOPS) {
    const chunk = stops.slice(i, i + MAX_STOPS);
    const last = chunk[chunk.length - 1];
    const origin = i > 0 ? `&origin=${stops[i - 1].lat},${stops[i - 1].lng}` : "";
    const way = chunk.slice(0, -1).map((j) => `${j.lat},${j.lng}`).join("|");
    out.push({ from: i + 1, to: i + chunk.length, url: `https://www.google.com/maps/dir/?api=1${origin}&destination=${last.lat},${last.lng}${way ? `&waypoints=${encodeURIComponent(way)}` : ""}&travelmode=driving` });
  }
  return out;
};

const RouteBar = ({ stops, located, title, testid = "partner-route-bar" }) => {
  const { pt } = usePT();
  if (!stops.length) return null;
  const parts = legs(stops);
  return (
    <div className="space-y-2 rounded-3xl border-2 border-[#2E9E6B] bg-white p-4" data-testid={testid}>
      <p className="font-bold text-[#0D3B2B]">{title || `${stops.length} stop${stops.length > 1 ? "s" : ""} on ${fmtDate(stops[0].date)}`}</p>
      <p className="text-xs text-[#4A6358]">{located ? "Sorted nearest-first from where you are, keeping time slots." : "Turn on location to sort from where you are."} About 20 minutes per stop.{parts.length > 1 && " Google Maps allows 10 stops per route, so your day is split into parts. Open the next part when you finish one."}</p>
      {parts.map((p, i) => (
        <a key={p.from} href={p.url} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#2E9E6B] font-bold text-white" data-testid={i ? `${testid}-nav-${i}` : "partner-navigate-all"}>
          <Navigation className="h-5 w-5" />{parts.length > 1 ? `${pt("Navigate")} ${p.from}–${p.to}` : pt("Navigate all stops in order")}</a>
      ))}
    </div>
  );
};

export const PickupsTab = ({ data, token, pos, onTrip, reload }) => {
  const { pt } = usePT();
  const overdue = data.jobs.filter((j) => j.overdue);
  const upcoming = data.jobs.filter((j) => !j.overdue);
  const days = [...new Set(upcoming.map((j) => j.date))];
  const [day, setDay] = useState(days[0]);
  const cur = days.includes(day) ? day : days[0];
  const stops = upcoming.filter((j) => j.date === cur);
  const job = (j) => <PartnerJob key={j.booking_id} j={j} token={token} materials={data.materials} flex={data.rate_flex_pct} onTrip={onTrip} reload={reload} />;
  return (
    <div className="space-y-4" data-testid="partner-pickups-tab">
      <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{pt("My pickups")}</h1>
      {overdue.length > 0 && (
        <section className="space-y-3 rounded-3xl bg-[#FDF1EC] p-3" data-testid="partner-overdue-section">
          <div className="px-1">
            <p className="flex items-center gap-2 font-display text-lg font-extrabold text-[#B4401F]"><AlertTriangle className="h-5 w-5" />Overdue · do these first ({overdue.length})</p>
            <p className="text-xs text-[#7A3A25]">These pickups are past their time slot. Complete them now, or tap "Can't complete" so the customer is informed.</p>
          </div>
          <RouteBar stops={overdue} located={!!pos} title={`${overdue.length} overdue stop${overdue.length > 1 ? "s" : ""}`} testid="partner-overdue-route" />
          {overdue.map(job)}
        </section>
      )}
      {days.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {days.map((d) => (
            <button key={d} onClick={() => setDay(d)} data-testid={`partner-day-${d}`}
              className={`h-10 shrink-0 rounded-full px-4 text-sm font-bold ${d === cur ? "bg-[#0D3B2B] text-white" : "border border-[#D2E0D8] bg-white text-[#4A6358]"}`}>
              {fmtDate(d)} · {upcoming.filter((j) => j.date === d).length}</button>
          ))}
        </div>
      )}
      <RouteBar stops={stops} located={!!pos} />
      {stops.map(job)}
      {!data.jobs.length && <p className="rounded-3xl bg-white p-6 text-center text-[#7B968B]" data-testid="partner-no-pickups">No pickups assigned right now.</p>}
    </div>
  );
};

export const MapTab = ({ data, pos }) => (
  <div className="space-y-3" data-testid="partner-map-tab">
    <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">Map</h1>
    <PartnerMap me={pos || (data.location?.lat != null ? data.location : null)} nearby={data.nearby || []} jobs={data.jobs} tall />
    {!pos && !data.location && <p className="rounded-2xl bg-white p-4 text-sm text-[#7B968B]">Turn on location to see the map around you.</p>}
    <BusyAreas areas={data.busy_areas} />
  </div>
);

const Row = ({ j, cancelled }) => (
  <div className="flex justify-between rounded-2xl bg-white p-3 text-sm" data-testid={`partner-${cancelled ? "cancelled" : "done"}-${j.booking_id}`}>
    <span><b>{j.booking_id}</b> · {j.customer}<br /><span className="text-xs text-[#7B968B]">{fmtDate(j.date)}{cancelled ? ` · ${j.slot_label}` : ` · ${j.total_kg} kg`}</span></span>
    {cancelled ? <span className="self-center rounded-full bg-[#FEE2E2] px-2 py-0.5 text-xs font-bold text-[#991B1B]">Cancelled</span>
      : <span className="text-right font-bold text-[#0D3B2B]">₹{j.amount_paid || 0}<br /><span className={`text-xs ${j.payment_status === "Paid" ? "text-[#2E9E6B]" : "text-[#E05C3A]"}`}>{j.payment_status}</span></span>}
  </div>
);

export const HistoryTab = ({ data }) => {
  const [v, setV] = useState("done");
  const rows = v === "done" ? data.done : data.cancelled || [];
  return (
    <div className="space-y-4" data-testid="partner-history-tab">
      <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">History</h1>
      <div className="grid grid-cols-2 gap-1 rounded-full bg-white p-1 shadow-sm">
        {[["done", `Completed (${data.done.length})`], ["cancelled", `Cancelled (${(data.cancelled || []).length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setV(k)} data-testid={`partner-history-${k}`} className={`h-10 rounded-full text-sm font-bold transition-colors ${v === k ? "bg-[#0D3B2B] text-white" : "text-[#4A6358]"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2" data-testid="partner-done-list">{rows.map((j) => <Row key={j.booking_id} j={j} cancelled={v === "cancelled"} />)}</div>
      {!rows.length && <p className="rounded-3xl bg-white p-6 text-center text-sm text-[#7B968B]">Nothing here yet.</p>}
    </div>
  );
};

export const MeTab = ({ data, token, reload, logout }) => {
  const { pt } = usePT();
  const [view, setView] = useState("");
  if (view === "profile") return <PartnerProfile token={token} onBack={() => { setView(""); reload(); }} />;
  return (
  <div className="space-y-4" data-testid="partner-me-tab">
    <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{data.name}</h1>
    <p className="flex flex-wrap items-center gap-2 text-sm text-[#4A6358]" data-testid="partner-me-mobile">{data.mobile}<VerifiedTick ok={data.mobile_verified} label={pt("Mobile verified")} testid="partner-mobile-verified" /></p>
    <LangSwitch />
    <button onClick={() => setView("profile")} className="flex w-full items-center justify-between rounded-3xl bg-white p-4 text-left shadow-sm" data-testid="partner-open-profile">
      <span><span className="block font-bold text-[#0D3B2B]">{pt("Profile & settings")}</span><span className="text-xs text-[#7B968B]">Vehicle, work area, timings, documents</span></span><ChevronRight className="h-5 w-5 text-[#7B968B]" />
    </button>
    <PartnerEarnings earnings={data.earnings} />
    <StarBanner star={data.star} count={data.star_count} />
    <PartnerPerformance perf={data.performance} />
    <Eligibility e={data.eligibility} />
    <ServiceHours h={data.service_hours} online />
    <PartnerAlerts token={token} vapid={data.vapid_public_key} />
    <BusyAreas areas={data.busy_areas} />
    {data.commission && data.commission.status !== "Free" && (
      <p className="rounded-2xl bg-white p-3 text-sm text-[#0D3B2B]" data-testid="partner-commission">
        {data.commission.status === "Due" ? `Kindsaver commission: ${data.commission.pct}% per completed pickup. Due now: ₹${data.commission.due}` : "Commission waived for you. Enjoy free pickups!"}</p>
    )}
    <PartnerRefer token={token} />
    <PartnerLoginSetup token={token} username={data.username} onDone={reload} />
    {data.rating && <MyRating r={data.rating} />}
    <PartnerKyc kyc={data.kyc} docs={data.kyc_docs} token={token} onDone={reload} />
    <RingTest />
    <a href={`tel:${data.support.phone.replace(/\s/g, "")}`} className="flex h-14 items-center justify-center gap-2 rounded-2xl border-2 border-[#D2E0D8] bg-white font-bold text-[#0D3B2B]" data-testid="partner-call-support"><Phone className="h-6 w-6" />{pt("Call Kindsaver office")}</a>
    <button onClick={logout} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-bold text-[#E05C3A]" data-testid="partner-logout-btn"><LogOut className="h-5 w-5" />{pt("Log out")}</button>
  </div>
  );
};
